/**
 * useAuth — login / logout / silent-refresh wired to TanStack Query.
 *
 * Q53 contract:
 *   - Refresh in httpOnly cookie (server-managed; FE never touches it).
 *   - Access in memory (fetcher.ts module-scope store).
 *   - Silent-refresh on app boot (App.tsx calls `silentRefresh` from this
 *     module before rendering routes).
 *
 * The login mutation:
 *   1. POSTs /auth/login (cookie set side-effect).
 *   2. **PRIVACY-FIRST CLEANUP** (Phase 5 FIX-PACK FB-8 / F-14):
 *      `clearFormDrafts()` + `qc.clear()` so any localStorage drafts
 *      and in-memory cache state from a previous actor on a shared
 *      device are wiped BEFORE we seed the new actor's session.
 *   3. Stores the returned access token in memory via setAccess.
 *   4. Seeds the auth.session cache + invalidates so useMe refetches
 *      with the new token.
 *
 * The logout mutation (Phase 5 W0.T0.7 hardened — DA SER-7 / R-12;
 * Phase 5 post-deploy F-01 — `clearAccess()` MOVED to `finally{}`):
 *   1. POST /auth/logout. `apiFetch` reads `getAccess()` from the
 *      module-scope store at request time and attaches an
 *      `Authorization: Bearer <token>` header automatically — see
 *      `lib/fetcher.ts` `apiFetch` headers block. The mechanism that
 *      makes this work is the *ordering* below, not any explicit
 *      header manipulation: `clearAccess()` lives in `finally{}` so
 *      `getAccess()` still returns the live token during the awaited
 *      POST. The earlier post-deploy implementation passed the captured
 *      token through an explicit `Authorization` header on the init
 *      argument, but `apiFetch` overwrites that header with the
 *      `getAccess()` value when access is non-null — so the explicit
 *      header was a no-op as long as the cleanup ordering was correct.
 *      Removed in post-deploy fix-pack FB-E (cluster E, 2026-05-10) to
 *      eliminate dead code and prevent the false impression that the
 *      explicit header is what authenticates the request.
 *   2. Best-effort: 4xx / 5xx / network drop are all swallowed — the
 *      server-side session can be GC'd by refresh-token rotation.
 *   3. **FAIL-CLOSED CLEANUP in `finally`.** Clear localStorage
 *      `aris.form.*` keys + clear in-memory access + clear the entire
 *      query cache. A network drop / 4xx / 5xx from /auth/logout must
 *      NEVER leave drafts on the device for the next user. The threat
 *      model (shared device, last actor's draft bleeds into next
 *      actor's session) outweighs the rare benefit of «keep state if
 *      logout fails»: cleanup runs unconditionally.
 *   4. Caller redirects to /login regardless of outcome (the mutation
 *      completes successfully even if the API call rejected).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiFetch, clearAccess, setAccess } from '@/lib/fetcher';
import { clearShownNotificationIds } from '@/lib/notification-seen';
import { qk } from '@/lib/query-keys';
import type { MeUser } from './use-me';

interface LoginResponse {
  accessToken: string;
  user: MeUser;
  accessTtlSeconds: number;
}

interface RefreshResponse {
  accessToken: string;
  accessTtlSeconds: number;
}

export interface LoginInput {
  email: string;
  password: string;
}

/**
 * Silent refresh on app boot. Fires once outside React (App.tsx invokes it
 * before rendering routes). Returns the populated MeUser on success so the
 * shell can prefetch and render in one frame, or null on failure (cold
 * start with no valid refresh cookie → render /login).
 *
 * NOTE: this function calls fetcher.apiFetch directly with the
 * `/auth/refresh` path (NOT `/auth/me`). The cookie is sent automatically
 * because apiFetch uses `credentials: 'include'`.
 */
export async function silentRefresh(): Promise<RefreshResponse | null> {
  try {
    const out = await apiFetch<RefreshResponse>('/auth/refresh', { method: 'POST' });
    setAccess(out.accessToken);
    return out;
  } catch {
    clearAccess();
    return null;
  }
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation<LoginResponse, unknown, LoginInput>({
    mutationFn: async (input) => {
      const res = await apiFetch<LoginResponse>('/auth/login', {
        method: 'POST',
        body: input,
      });
      return res;
    },
    onSuccess: (res) => {
      // Phase 5 FIX-PACK FB-8 (closes F-14 / UI-DA S-6 + CR-DA S-7):
      // wipe any localStorage drafts left by a previous actor on a
      // shared device. Runs BEFORE we seed the new actor's session in
      // the cache so the next render sees a clean form-state surface.
      // Also clear the in-memory query cache for the same reason —
      // any stale per-user data (workload pages, employee lists) from
      // the prior session must not bleed into the new actor's view.
      clearFormDrafts();
      qc.clear();
      setAccess(res.accessToken);
      // Seed the cache directly so useMe doesn't need a refetch on the
      // very first render after login.
      qc.setQueryData(qk.auth.session(), res.user);
      qc.invalidateQueries({ queryKey: qk.auth.session() });
    },
  });
}

/**
 * Strip every `aris.form.*` key from localStorage. The hook
 * `useFormStatePersist` (Phase 5 FI-12) writes drafts under
 * `aris.form.${formKey}.${entityId ?? 'new'}.${actorId}` so a single
 * prefix sweep is sufficient. Iterates backwards because removeItem
 * mutates the index space.
 *
 * Phase 5 FIX-PACK FB-8 (closes F-14 / UI-DA S-6 + CR-DA S-7): called
 * on BOTH login and logout. Login-side fires before the cache is
 * seeded with the new actor's session, so any drafts left by the
 * previous actor on a shared device are wiped before the next user
 * sees a form. The actor-id namespacing on draft keys gives some
 * defence-in-depth, but the previous actor's id may match by luck on
 * a kiosk / shared workstation — wiping unconditionally is the
 * privacy-correct default.
 *
 * Defensive: tolerates SSR / no-window / quota-blocked localStorage.
 */
function clearFormDrafts(): void {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return;
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith('aris.form.')) {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    // localStorage can throw on quota / private-mode / SecurityError.
    // Logout MUST NOT fail because of this — best-effort.
  }
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation<void, unknown, void>({
    mutationFn: async () => {
      // Post-deploy fix-pack FB-E (cluster E, 2026-05-10):
      //
      // The REAL fix for F-01 is the ordering below: `clearAccess()`
      // lives in `finally{}` so `apiFetch` reads the live access
      // token from `getAccess()` while the POST is in flight, and
      // attaches it as `Authorization: Bearer <token>` automatically
      // (see `lib/fetcher.ts:apiFetch`). The earlier post-deploy
      // patch ALSO passed an explicit `Authorization` header on the
      // init arg, but `apiFetch` overwrites that header with the
      // value from `getAccess()` whenever access is non-null — so the
      // explicit header was dead code, harmless but misleading. It is
      // removed here to make the actual mechanism (clear AFTER the
      // api call so `apiFetch` reads the live token via `getAccess()`)
      // legible.
      try {
        await apiFetch<void>('/auth/logout', { method: 'POST' });
      } catch {
        // Best-effort. Cookie/refresh-token rotation will catch up on
        // the next login. Swallow so the mutation resolves and callers
        // can redirect deterministically.
      } finally {
        // Phase 5 W0.T0.7 (DA SER-7 / R-12) — fail-closed cleanup runs
        // UNCONDITIONALLY. The order matters: this block runs AFTER
        // the awaited POST completes (or rejects), so `getAccess()`
        // returned the live token to `apiFetch` above. Order inside
        // this block doesn't matter: the API call already completed.
        clearFormDrafts();
        // Блок 2 · Ф7 · В11-a, фикс-пак 2: на выходе забывается только список
        // показанных строк ВЫШЕДШЕГО пользователя. ⚠️ Метка «видел до» ОСТАЁТСЯ
        // (фикс-пак 1 стирал и её — и счётчик «новых» обнулялся у каждого, кто
        // вечером нажал «Выйти»); память других пользователей браузера не
        // трогается. Идентификатор берётся из кэша ДО его очистки строкой ниже.
        const leaving = qc.getQueryData<{ id?: unknown }>(qk.auth.session())?.id;
        if (typeof leaving === 'string') clearShownNotificationIds(leaving);
        clearAccess();
        qc.clear();
      }
    },
  });
}

/**
 * Convenience aggregator — returns the three primitives in one hook so
 * pages can `const { login, logout, silentRefresh } = useAuth()`.
 *
 * Note: the silentRefresh is the same module function (not a hook
 * factory), exposed here for ergonomic call-sites. App.tsx's boot path
 * imports it directly to avoid a hook-in-effect dance.
 */
export function useAuth() {
  return {
    login: useLogin(),
    logout: useLogout(),
    silentRefresh,
  };
}
